import { z } from 'zod';

import { TypeDynamicPage } from '../models/contentful-generated';
import { ContentfulSysSchema } from './contentful-sys.validator';
import { LandingPageEntrySchema } from './landing-page.validator';

export const DynamicPageEntrySchema = z.object({
  sys: ContentfulSysSchema,
  fields: z.looseObject({
    internalName: z.string(),
    contentType: z.string(),
    parentPage: LandingPageEntrySchema.optional().nullable(),
    route: z.string().optional().nullable(),
    slug: z.string().optional().nullable(),
    routeIds: z.array(z.string()).optional().nullable(),
    accessType: z.enum(['Authenticated Only', 'CSR Only', 'Public']),
    theme: z.string(),
    pageContent: z.array(z.any()).optional().nullable(),
    translations: z.record(z.string(), z.any()).optional().nullable()
  }),
  metadata: z
    .object({
      tags: z.array(z.any()).optional().nullable()
    })
    .optional()
    .nullable()
});

export function validateDynamicPageEntry(data: TypeDynamicPage) {
  return DynamicPageEntrySchema.safeParse(data);
}

/**
 * Returns validation result.
 *
 * @param data
 */
export function validateDynamicPageEntries(data: TypeDynamicPage[]) {
  return z.array(DynamicPageEntrySchema).safeParse(data);
}

/**
 * Returns valid entries only. In most cases, this is preferred,
 * It allows us to still show some entries instead of showing nothing when only a single entry is invalid.
 *
 * @param data
 */
export function validateAndFilterDynamicPageEntries(data: TypeDynamicPage[]) {
  if (!data) return [];

  return data.reduce((acc: TypeDynamicPage[], entry) => {
    const validationResult = validateDynamicPageEntry(entry);
    if (validationResult.success) {
      acc.push(validationResult.data as unknown as TypeDynamicPage);
    } else {
      console.warn(
        `Validation failed for dynamic page entry with id: '${entry?.sys?.id}'. It will be filtered out. Error: ${validationResult.error.message}`
      );
    }
    return acc;
  }, []);
}

/**
 * Slim schema for dynamic page entries fetched via the page-picker query.
 * Only validates the fields that are explicitly selected in that query
 * (sys, internalName, route, accessType, theme, contentType) — all other fields are absent from the response.
 */
const DynamicPagePickerEntrySchema = z.object({
  sys: ContentfulSysSchema,
  fields: z.looseObject({
    internalName: z.string(),
    contentType: z.string(),
    route: z.string().optional().nullable(),
    accessType: z.enum(['Authenticated Only', 'CSR Only', 'Public']),
    theme: z.string()
  }),
  metadata: z
    .object({
      tags: z.array(z.any()).optional().nullable()
    })
    .optional()
    .nullable()
});

/**
 * Returns valid picker-summary entries only, filtering out any entries whose
 * required subset of fields is missing or malformed.
 */
export function validateAndFilterDynamicPagePickerEntries(data: TypeDynamicPage[]) {
  if (!data) return [];

  return data.reduce((acc: TypeDynamicPage[], entry) => {
    const validationResult = DynamicPagePickerEntrySchema.safeParse(entry);
    if (validationResult.success) {
      acc.push(validationResult.data as unknown as TypeDynamicPage);
    } else {
      console.warn(
        `Validation failed for dynamic page picker entry with id: '${entry?.sys?.id}'. It will be filtered out. Error: ${validationResult.error.message}`
      );
    }
    return acc;
  }, []);
}

